import type React from 'react';
import { parseCsv } from '../../lib/csv-table';
import { Markdown } from '../Markdown';
import { sandboxedHtmlDocument } from '../../lib/sandboxed-html';

/**
 * A miniature of the real document, not a description of it.
 *
 * A thumbnail's job is to be recognisable at a glance, and what makes a document
 * recognisable is its SHAPE — headings, bullets, a table's grid.
 *
 * So the content renders for real and is then scaled down, rather than being reduced to text first:
 *
 *  - markdown → the same `Markdown` component the viewer uses, at `SCALE`;
 *  - html → the page itself in a sandboxed iframe, at `SCALE`;
 *  - csv → the real table, because a sheet's shape is its grid;
 *  - plain text and json → the pre-formatted lines, which ARE that file's real shape.
 *
 * The scale trick rather than a small font: shrinking type alone loses the proportions — a heading stops
 * looking like a heading. `transform: scale` keeps every relationship and just makes the page small, which
 * is what a thumbnail of a document should be.
 */
const SCALE = 0.34;

/** Rows parsed for a CSV thumbnail. Far below `MAX_CSV_ROWS`: the card clips after a dozen or so, so parsing a
 *  2000-row export per card would be work whose result is never drawn — and there is one of these per card. */
const PREVIEW_ROWS = 40;

export function RichFilePreview({
  kind,
  body,
  lines,
}: {
  kind: 'markdown' | 'html' | 'csv' | 'text';
  /** The document's own source, for the kinds that render it. */
  body: string;
  /** The pre-extracted lines, for the text-shaped kinds and as the html fallback. */
  lines: string[];
}) {
  const inner: React.CSSProperties = {
    // The unscaled page is laid out at 1/SCALE of the box, so after scaling it fills exactly — this is
    // what makes the miniature show a page's worth of content rather than a magnified corner.
    width: `${100 / SCALE}%`,
    height: `${100 / SCALE}%`,
    transform: `scale(${SCALE})`,
    transformOrigin: 'top left',
    pointerEvents: 'none',
  };

  if (kind === 'html') {
    return (
      <div style={styles.page}>
        <iframe
          // Two independent controls, because neither covers the other. `sandbox` with NO allow-scripts stops
          // the page running JS, submitting forms or navigating; the CSP in `sandboxedHtmlDocument` stops it
          // FETCHING anything. Sandbox alone still allows passive subresources, so a remote `<img>` would beacon
          // out the moment the card scrolled into view.
          sandbox=""
          srcDoc={sandboxedHtmlDocument(body)}
          title=""
          aria-hidden
          scrolling="no"
          style={{ ...inner, border: 'none', background: 'var(--ds-white)' }}
        />
      </div>
    );
  }

  if (kind === 'markdown') {
    return (
      <div style={styles.page}>
        <div style={{ ...inner, padding: 16, overflow: 'hidden' }}>
          {/* `gfm` because a report's tables ARE its shape — the `compact` variant parses no GFM by default,
              which left `| a | b |` rendering as literal pipes: the raw source this preview exists to replace. */}
          <Markdown variant="compact" dir="auto" gfm>
            {body}
          </Markdown>
        </div>
      </div>
    );
  }

  if (kind === 'csv') {
    const table = parseCsv(body, { maxRows: PREVIEW_ROWS });
    return (
      <div style={styles.page}>
        <div style={{ ...inner, padding: 12, overflow: 'hidden' }}>
          <table style={styles.table}>
            {/* The header only renders when the file HAS one: `parseCsv` decides that by heuristic, and
                labelling a first row of data as headings would misreport the file. */}
            {table.header && (
              <thead>
                <tr>
                  {table.header.map((cell, i) => (
                    <th key={i} style={{ ...styles.cell, ...styles.headCell }}>
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} style={styles.cell}>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.page, padding: 12, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {lines.map((line, i) => (
        <span
          key={i}
          style={{
            fontSize: 11,
            lineHeight: 1.35,
            color: 'var(--ds-text-secondary)',
            whiteSpace: 'pre',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {line}
        </span>
      ))}
    </div>
  );
}

const styles = {
  page: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    background: 'var(--ds-white)',
    // Inherit the well's rounding so a miniature never squares off the card's corners.
    borderRadius: 'inherit',
    boxSizing: 'border-box',
  } satisfies React.CSSProperties,
  table: {
    borderCollapse: 'collapse',
    // `fixed` so one long cell cannot stretch the grid wider than the card and hide every other column.
    tableLayout: 'fixed',
    width: '100%',
    fontSize: 11,
    lineHeight: 1.35,
  } satisfies React.CSSProperties,
  cell: {
    border: '1px solid var(--ds-border-light-default)',
    padding: '2px 4px',
    textAlign: 'start',
    color: 'var(--ds-text-secondary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  } satisfies React.CSSProperties,
  headCell: {
    fontWeight: 600,
    color: 'var(--ds-text-primary)',
    background: 'var(--ds-surface-light-gray)',
  } satisfies React.CSSProperties,
};
